import { useEffect, useRef, useState } from "react";
import { api, apiError } from "./api";
import { Card, PageHead, Pill, Btn, Input, Select, Field, Confirm, Empty } from "./widgets";
import { toast } from "sonner";
import { Upload, Search } from "lucide-react";

const CATEGORIES = ["Homepage", "Founder", "Services", "Blog", "Lake Nona", "Business", "Financial", "Temporary", "Other"];

const SLOT_LABELS = {
  hero: "Homepage — Hero",
  staircase: "Home — More Than Numbers",
  marble: "Services — Texture / Marble",
  office: "About — Founder / Service cards",
  meeting: "About — Office / Consultation",
  leader: "Author page portrait",
  workspace: "Blog — Workspace",
  laptop: "Blog — Laptop",
};

const MediaLibrary = () => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(null);
  const [assignSlot, setAssignSlot] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const fileRef = useRef(null);
  const [uploadMeta, setUploadMeta] = useState({ title: "", alt: "", category: "Other" });

  const load = () =>
    api.get(`/admin/media?q=${encodeURIComponent(q)}&category=${encodeURIComponent(category)}`)
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, category]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", uploadMeta.title || file.name);
    fd.append("alt", uploadMeta.alt);
    fd.append("category", uploadMeta.category);
    try {
      await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded (converted to WebP). It is NOT marked as demo.");
      setUploadMeta({ title: "", alt: "", category: "Other" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveMeta = async () => {
    try {
      await api.patch(`/admin/media/${selected.id}`, {
        title: selected.title, alt: selected.alt, caption: selected.caption,
        category: selected.category, focal: selected.focal,
      });
      toast.success("Saved.");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const assign = async () => {
    if (!assignSlot) return;
    try {
      await api.post(`/admin/media/${selected.id}/assign`, { slot: assignSlot });
      toast.success(`Image now live in "${SLOT_LABELS[assignSlot]}" — demo badge removed.`);
      setSelected(null);
      setAssignSlot("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/media/${selected.id}`);
      toast.success("Image deleted.");
      setConfirmDel(false);
      setSelected(null);
      load();
    } catch (e) {
      setConfirmDel(false);
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="media-library">
      <PageHead
        title="Media Library"
        sub="Upload, organize and assign photography. Demo images carry a small on-site badge until replaced."
        actions={
          <>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            <Btn primary onClick={() => fileRef.current.click()} data-testid="media-upload">
              <Upload className="h-3.5 w-3.5" /> Upload Image
            </Btn>
          </>
        }
      />

      <div className="mb-6 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#171717]/35" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search media…" className="pl-9" data-testid="media-search" />
        </div>
        <Select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="media-category">
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </Select>
      </div>

      {!items ? (
        <p className="text-sm text-[#171717]/50">Loading…</p>
      ) : items.length === 0 ? (
        <Empty>No media found.</Empty>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {items.map((m) => (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              data-testid={`media-item-${m.id}`}
              className="group relative overflow-hidden rounded-[3px] border border-[#171717]/10 bg-white text-left transition-colors hover:border-[#C6A46A]"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#171717]/5">
                <img src={m.url} alt={m.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {m.isDemo && (
                  <span className="absolute left-2 top-2 rounded-[2px] bg-[#171717]/85 px-2 py-1 text-[0.5rem] font-medium uppercase tracking-[0.16em] text-[#C6A46A]">
                    Demo image
                  </span>
                )}
              </div>
              <div className="p-3">
                <p className="truncate text-sm text-[#171717]">{m.title}</p>
                <p className="mt-0.5 text-[0.62rem] uppercase tracking-[0.14em] text-[#171717]/40">{m.category}</p>
                {m.usedOn?.length > 0 && (
                  <p className="mt-1 truncate text-[0.62rem] text-[#8a6f3e]">Used on: {m.usedOn.join(", ")}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-[80] bg-[#171717]/60 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-4" role="dialog" aria-modal="true" data-testid="media-detail">
          <div className="flex h-[100dvh] w-full flex-col overflow-hidden bg-white sm:h-auto sm:max-h-[90vh] sm:max-w-3xl sm:rounded-[3px]">
            <div className="flex items-center justify-between border-b border-[#171717]/10 px-5 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))] sm:pt-3">
              <p className="text-[0.62rem] font-medium uppercase tracking-[0.18em] text-[#171717]/50">Media detail</p>
              <button onClick={() => setSelected(null)} aria-label="Close" data-testid="media-detail-close"
                className="flex min-h-[44px] min-w-[44px] items-center justify-center text-[#171717]/60 hover:text-[#171717]">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 sm:p-7">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="relative overflow-hidden rounded-[3px] bg-[#171717]/5">
                <img
                  src={selected.url}
                  alt={selected.alt}
                  className="w-full object-cover"
                  style={{ objectPosition: `${selected.focal?.x ?? 50}% ${selected.focal?.y ?? 50}%` }}
                />
                {selected.isDemo && <Pill tone="champagne">Demo image — temporary</Pill>}
              </div>
              <div className="space-y-4">
                <Field label="Title"><Input value={selected.title} onChange={(e) => setSelected({ ...selected, title: e.target.value })} /></Field>
                <Field label="Alt text"><Input value={selected.alt} onChange={(e) => setSelected({ ...selected, alt: e.target.value })} /></Field>
                <Field label="Caption"><Input value={selected.caption || ""} onChange={(e) => setSelected({ ...selected, caption: e.target.value })} /></Field>
                <Field label="Category">
                  <Select value={selected.category} onChange={(e) => setSelected({ ...selected, category: e.target.value })} className="w-full">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </Field>
                <Field label={`Focal point — horizontal ${selected.focal?.x ?? 50}%`}>
                  <input type="range" min="0" max="100" value={selected.focal?.x ?? 50}
                    onChange={(e) => setSelected({ ...selected, focal: { ...selected.focal, x: Number(e.target.value) } })}
                    className="w-full accent-[#C6A46A]" />
                </Field>
                <Field label={`Focal point — vertical ${selected.focal?.y ?? 50}%`}>
                  <input type="range" min="0" max="100" value={selected.focal?.y ?? 50}
                    onChange={(e) => setSelected({ ...selected, focal: { ...selected.focal, y: Number(e.target.value) } })}
                    className="w-full accent-[#C6A46A]" />
                </Field>
                {selected.usedOn?.length > 0 && (
                  <p className="text-[0.68rem] text-[#8a6f3e]">Used on: {selected.usedOn.join(", ")}</p>
                )}
              </div>
            </div>

            <div className="mt-6 border-t border-[#171717]/10 pt-5">
              <Field label="Use this image on the website (replaces the current image in that slot; demo badge disappears)">
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select value={assignSlot} onChange={(e) => setAssignSlot(e.target.value)} className="flex-1" data-testid="media-assign-slot">
                    <option value="">Select a website slot…</option>
                    {Object.entries(SLOT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </Select>
                  <Btn primary onClick={assign} disabled={!assignSlot} data-testid="media-assign-btn">Replace Image</Btn>
                </div>
              </Field>
            </div>
            </div>

            <div className="grid grid-cols-2 gap-2 border-t border-[#171717]/10 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:flex sm:justify-between sm:border-0 sm:p-0 sm:pt-5">
              <Btn danger onClick={() => setConfirmDel(true)} data-testid="media-delete">Delete</Btn>
              <Btn primary onClick={saveMeta} data-testid="media-save">Save</Btn>
            </div>
          </div>
        </div>
      )}

      <Confirm
        open={confirmDel}
        title="Delete this image?"
        body="The file will be permanently removed. Images currently used on the website cannot be deleted."
        confirmLabel="Delete permanently"
        onConfirm={remove}
        onCancel={() => setConfirmDel(false)}
      />
    </div>
  );
};

export default MediaLibrary;
